import { Table } from 'antd';
import { Button, Message } from 'fave-ui';
import { PageHeaderWithSpace } from '../../components/common';
import { ColumnsType } from 'antd/lib/table';
import {
  Details,
  useFetchStatements,
} from '../../services/Statements/useFetchStatements';
import useSetBreadCrumbParams from '../../hooks/useSetBreadCrumbParams';
import { useParams } from 'react-router';
import useDynamicTableHeight from '../../hooks/useDynamicTableHeight';
import { useMemo, useRef, useState } from 'react';
import { useVT } from 'virtualizedtableforantd4';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import { calcTableHeight } from '../../utils/utilFunctions';
import useOnceCallback from '../../hooks/useOnceCallback';

import { useUserContext } from '../../contexts/UserContext';
import { usePostStatementsToEmail } from '../../services/Statements/usePostStatementsToEmail';

const elementIds = {
  sendToMeBtn: 'sendToMeBtn',
};

const tableHeight = calcTableHeight(300);

const StatementsView = () => {
  const { user } = useUserContext();
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { tableHeight: dynamicTableHeight } = useDynamicTableHeight(tableRef);
  const { id, bank_account_id: bankAccountId } = useParams();
  const hasIds = !!(id && bankAccountId);
  const {
    data: statementsData,
    isFetching: isFetchStatementsLoading,
    fetchNextPage,
  } = useFetchStatements({
    finance_account_id: parseInt(String(bankAccountId)),
  });
  const [outletNames, setOutletNames] = useState<string>(
    'Loading outlet name...',
  );
  const { mutateAsync: postStatementsToEmail } = usePostStatementsToEmail();
  const [emailStatementsLoading, setEmailStatementsLoading] = useState('');

  // to set outlet names
  useOnceCallback(() => {
    if (!isFetchStatementsLoading) {
      const flatMapOutletNames = statementsData?.pages.flatMap(
        data => data.outlets_name,
      );
      const joinedOutletsNames = flatMapOutletNames?.join(', ');
      if (joinedOutletsNames) setOutletNames(joinedOutletsNames);
      else setOutletNames('No outlets');
    }
  }, !isFetchStatementsLoading);

  const flatMapFetchStatements = useMemo(
    () => statementsData?.pages.flatMap(data => data.statements),
    [statementsData],
  );

  const flatMapStatements = useMemo(
    () => flatMapFetchStatements?.flatMap(data => data.details),
    [flatMapFetchStatements],
  );

  const userEmail = useMemo(() => user?.email || '', [user]);

  const handlePostEmailToStatements = (
    referenceNumber: string,
    periodEndDate: string,
  ) => {
    setEmailStatementsLoading(referenceNumber);
    postStatementsToEmail(
      {
        company_id: parseInt(String(id)),
        list_of_statement_reference_number: [referenceNumber],
        list_of_statement_period_end_date: [periodEndDate],
        email: userEmail,
      },
      {
        onSuccess: async response =>
          Message.success({ content: response.data.message }),
        onSettled: () => setEmailStatementsLoading(''),
      },
    );
  };

  useSetBreadCrumbParams(
    [
      { label: '', key: String(id) },
      {
        label: '',
        key: String(bankAccountId),
      },
    ],
    hasIds,
  );

  const columns: ColumnsType<Details> = [
    {
      title: 'Reference No',
      dataIndex: 'reference_no',
      width: 120,
      render: (referenceNo: string) => <div>{referenceNo}</div>,
    },
    {
      title: 'Periods',
      dataIndex: 'period',
      width: 120,
      render: (periods: string) => <div>{periods}</div>,
    },
    {
      title: 'Amount',
      dataIndex: 'formatted_amount',
      width: 70,
      render: (amount: string) => <div>{amount}</div>,
    },
    {
      title: 'Actions',
      align: 'center',
      dataIndex: '',
      width: 100,
      render: (statementDetails: Details) => {
        const isSendEmailLoading =
          emailStatementsLoading === statementDetails.reference_no;
        return (
          <Button
            loading={isSendEmailLoading}
            data-id={`${elementIds.sendToMeBtn}-${statementDetails.reference_no}`}
            key={statementDetails.reference_no}
            type={'primary'}
            onClick={() =>
              handlePostEmailToStatements(
                statementDetails.reference_no,
                statementDetails.period_end_date,
              )
            }
          >
            Send to me
          </Button>
        );
      },
    },
  ];

  // prevents user from calling api many times
  const handleDebouncedFetchNextPage = useDebouncedCallback(fetchNextPage, 300);

  // prevents user from calling api many times
  const handleDebouncedImmediateFetchNextPage = useDebouncedCallback(
    fetchNextPage,
    300,
  );

  const tableBodyHeight =
    tableRef.current?.getElementsByClassName('ant-table-tbody')[0]
      .clientHeight || 0;

  // boolean to trigger load more
  const shouldImmediatelyLoadMore = useMemo(
    () => tableBodyHeight < dynamicTableHeight,
    [tableBodyHeight, tableBodyHeight],
  );

  if (shouldImmediatelyLoadMore) handleDebouncedImmediateFetchNextPage();

  const [virtualTable] = useVT(
    () => ({
      onScroll: ({ isEnd }) => {
        if (isEnd) handleDebouncedFetchNextPage();
      },
      scroll: { y: tableHeight },
      offset: 50,
    }),
    [scroll, handleDebouncedFetchNextPage, tableHeight],
  );

  return (
    <>
      <div ref={tableRef}>
        <PageHeaderWithSpace title={'Statements'} subTitle={outletNames} />
        <Table
          dataSource={flatMapStatements}
          rowKey={row => row.reference_no}
          columns={columns}
          loading={isFetchStatementsLoading}
          scroll={{ y: tableHeight }}
          pagination={false}
          components={virtualTable}
        />
      </div>
    </>
  );
};

export default StatementsView;
